/**
 * Mercado: consultar el escaparate y operar sobre él.
 *
 * Ninguna mutación escribe en tablas. Publicar, retirar y comprar son funciones
 * RPC porque cada una tiene que hacer varias cosas a la vez o ninguna — cobrar,
 * pagar, cambiar de dueño, cerrar el anuncio — y porque el usuario debe salir de
 * `auth.uid()` en el servidor y no de un parámetro que envíe el cliente.
 *
 * El escaparate se refresca solo con Realtime. Es la única pantalla del juego
 * donde el dato cambia por acciones de otras personas: una carta puede venderse
 * mientras la estás mirando, y enterarte al pulsar Comprar es la peor forma.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { queryKeys } from '@/services/queryClient';
import { describeDatabaseError, requireSupabase, supabase } from '@/services/supabaseClient';
import type { DbMarketBrowse, DbMarketListing, DbUserCard } from '@/types/database';
import type { CollectionCard, Rarity } from '@/types/game';

export interface MarketOffer {
  listingId: string;
  priceGold: number;
  createdAt: string;
  sellerId: string;
  sellerUsername: string;
  userCardId: string;
  masterId: string;
  level: number;
  serialNumber: number | null;
  rarity: Rarity;
  maxSupply: number | null;
}

function toOffer(row: DbMarketBrowse): MarketOffer {
  return {
    listingId: row.listing_id,
    priceGold: row.price_gold,
    createdAt: row.created_at,
    sellerId: row.seller_id,
    sellerUsername: row.seller_username,
    userCardId: row.user_card_id,
    masterId: row.card_master_id,
    level: row.level,
    serialNumber: row.serial_number,
    rarity: row.rarity,
    maxSupply: row.max_supply,
  };
}

/** Los mensajes de las RPC ya están escritos para el jugador; no se retraducen. */
function toReadableError(error: { code?: string; message: string }): Error {
  const isOurs = error.code === 'P0001' || /^[A-ZÁÉÍÓÚÑ¡]/.test(error.message);
  return new Error(isOurs ? error.message : describeDatabaseError(error));
}

/* -------------------------------------------------------------------------- */

export function useMarketOffers() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.marketListings(),
    enabled: Boolean(supabase),
    // Corto a propósito: el precio de una carta es un dato que caduca rápido.
    staleTime: 15_000,
    queryFn: async (): Promise<MarketOffer[]> => {
      const { data, error } = await supabase!
        .from('market_browse')
        .select('*')
        .order('price_gold', { ascending: true })
        .limit(200);
      if (error) throw new Error(describeDatabaseError(error));
      return data.map(toOffer);
    },
  });

  useEffect(() => {
    if (!supabase) return;
    // Cualquier cambio en los anuncios invalida el escaparate. No se intenta
    // aplicar el cambio a mano sobre la caché: la vista `market_browse` cruza
    // cuatro tablas y reconstruirla en el cliente sería duplicar ese join.
    const channel = supabase
      .channel('market-listings')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'market_listings' },
        () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.marketListings() });
        },
      )
      .subscribe();

    return () => {
      void supabase!.removeChannel(channel);
    };
  }, [queryClient]);

  return query;
}

/** Anuncios propios, incluidos los cerrados: es el historial de ventas. */
export function useMyListings(userId: string | undefined) {
  return useQuery({
    queryKey: ['market', 'mine', userId] as const,
    enabled: Boolean(userId && supabase),
    queryFn: async (): Promise<DbMarketListing[]> => {
      const { data, error } = await supabase!
        .from('market_listings')
        .select('*')
        .eq('seller_id', userId!)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw new Error(describeDatabaseError(error));
      return data;
    },
  });
}

function invalidateAfterTrade(queryClient: ReturnType<typeof useQueryClient>, userId?: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.marketListings() });
  void queryClient.invalidateQueries({ queryKey: ['market', 'mine', userId] });
  if (!userId) return;
  void queryClient.invalidateQueries({ queryKey: queryKeys.inventory(userId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId) });
}

export function useCreateListing(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ cardId, price }: { cardId: string; price: number }) => {
      const { data, error } = await requireSupabase().rpc('create_market_listing', {
        p_user_card_id: cardId,
        p_price_gold: price,
      });
      if (error) throw toReadableError(error);
      return data;
    },
    onSuccess: () => invalidateAfterTrade(queryClient, userId),
  });
}

export function useCancelListing(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (listingId: string) => {
      const { data, error } = await requireSupabase().rpc('cancel_market_listing', {
        p_listing_id: listingId,
      });
      if (error) throw toReadableError(error);
      return data;
    },
    onSuccess: () => invalidateAfterTrade(queryClient, userId),
  });
}

export function useBuyListing(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (listingId: string): Promise<CollectionCard> => {
      const { data, error } = await requireSupabase().rpc('execute_market_purchase', {
        p_listing_id: listingId,
      });
      if (error) throw toReadableError(error);
      const row = data as unknown as DbUserCard;
      return {
        id: row.id,
        masterId: row.card_master_id,
        level: row.level,
        xp: row.xp,
        isTradable: row.is_tradable,
        serialNumber: row.serial_number,
        acquiredAt: row.created_at,
      };
    },
    // Sin reintento: reintentar una compra a ciegas es como se cobra dos veces.
    retry: false,
    onSuccess: () => invalidateAfterTrade(queryClient, userId),
  });
}
